import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import './RollingWindowGraph.css';

import { getQueueGraphSnapshot } from '../utils/fetchTransformQueue';
import { buildRollingWindowGraphData, RollingGraphPoint, RollingGraphSeries } from '../domain/rollingWindowGraph';

const SVG_WIDTH = 1200;
const SVG_HEIGHT = 360;
const MAX_SERIES = 8;
const WINDOW_SECONDS = 120;

function toPath(points: RollingGraphPoint[], yMin: number, yMax: number): string {
  if (points.length === 0) {
    return '';
  }

  const xFromSeconds = (xSeconds: number) => ((xSeconds + WINDOW_SECONDS / 2) / WINDOW_SECONDS) * SVG_WIDTH;
  const yFromValue = (value: number) => {
    if (yMax === yMin) {
      return SVG_HEIGHT / 2;
    }
    const normalized = (value - yMin) / (yMax - yMin);
    // Inverted orientation: top-down to match satellite measurement direction.
    return normalized * SVG_HEIGHT;
  };

  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xFromSeconds(point.xSeconds)} ${yFromValue(point.yValue)}`)
    .join(' ');
}

const RollingWindowGraph: React.FC = () => {
  const [series, setSeries] = useState<RollingGraphSeries[]>([]);
  const [playbackHeadTimeIso, setPlaybackHeadTimeIso] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => {
      const snapshot = getQueueGraphSnapshot();
      const graphData = buildRollingWindowGraphData(
        snapshot.processedDataQueue,
        snapshot.playbackHeadTimeIso,
        WINDOW_SECONDS
      );
      const selectedSeries = graphData.series.filter((entry) => {
        const route = snapshot.categoryRouting?.[entry.categoryId];
        return route ? Boolean(route.enabled) : true;
      });
      setSeries(selectedSeries.slice(0, MAX_SERIES));
      setPlaybackHeadTimeIso(snapshot.playbackHeadTimeIso);
    };

    refresh();
    const interval = window.setInterval(refresh, 250);
    return () => window.clearInterval(interval);
  }, []);

  const yDomain = useMemo(() => {
    const allValues = series.flatMap((item) => item.points.map((point) => point.yValue));
    if (allValues.length === 0) {
      return { min: 0, max: 241 };
    }
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    if (min === max) {
      return { min: min - 1, max: max + 1 };
    }
    return { min, max };
  }, [series]);

  const hasData = series.some((item) => item.points.length > 0);

  return (
    <div className="rwGraphShell">
      <div className="rwGraphTop">
        <h2>Rolling Window Graph</h2>
        <div className="rwGraphMeta">
          <span>Playhead (center): {playbackHeadTimeIso ? dayjs(playbackHeadTimeIso).format('YYYY-MM-DD HH:mm:ss[Z]') : 'Waiting for playback...'}</span>
        </div>
      </div>

      <div className="rwGraphBody">
        <div className="rwGraphCanvasWrap">
          <svg viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} preserveAspectRatio="none" className="rwGraphSvg" role="img" aria-label="Rolling window category graph">
            <line x1={SVG_WIDTH / 2} y1={0} x2={SVG_WIDTH / 2} y2={SVG_HEIGHT} className="rwPlayhead" />
            <line x1={0} y1={SVG_HEIGHT - 1} x2={SVG_WIDTH} y2={SVG_HEIGHT - 1} className="rwAxis" />
            {series.map((entry) => (
              <path
                key={entry.categoryId}
                d={toPath(entry.points, yDomain.min, yDomain.max)}
                stroke={entry.color}
                className="rwLine"
              />
            ))}
          </svg>
          {!hasData && (
            <div className="rwGraphEmpty">
              Press Start in the Player tab to fill the queue and begin realtime graph updates.
            </div>
          )}
        </div>
        <div className="rwLegend">
          {series.map((entry) => (
            <div key={entry.categoryId} className="rwLegendItem">
              <span className="rwLegendSwatch" style={{ backgroundColor: entry.color }} />
              <span className="rwLegendLabel">{entry.categoryLabel}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RollingWindowGraph;
